/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

$('.js-custom-wishlist-button').on('click',function (){
  $(this).toggleClass('active');
  $('.js-product-wishlist-btn').find('button').click();
});
$('.js-product-wishlist-btn').on('DOMSubtreeModified',function (){
  const svg = $(this).find('svg');
  $('.js-media-gallery-wishlist').show();
  if(svg.attr('data-prefix') == 'fas'){
    $('.js-custom-wishlist-button').addClass('active');
  }else{
    $('.js-custom-wishlist-button').removeClass('active');
  }
})
$(".js-star-rating").click(function() {
  $(".js-review-tab a").click();
  $([document.documentElement, document.body]).animate({
    scrollTop: $(".js-review-tab").offset().top
  }, 1500);
});


window.addEventListener("DOMContentLoaded", () => {
  // Display the block with the price above the product name
  let targetElements = document.querySelectorAll(".col-xs-12.col-md-7");
  let targetElement = targetElements[0];

  if (targetElement) {
    let highlightWrapper = targetElement.querySelector(".gr-highlight__wrapper");
    let priceWrapper = document.querySelector(".gr-wrapper__price");

    if (highlightWrapper && priceWrapper) {
      highlightWrapper.parentNode.insertBefore(priceWrapper, highlightWrapper.nextSibling);
    }
  }

  // Checkbox is checked by default
  let termsCheckInputs = document.querySelectorAll("input.terms-check");

  termsCheckInputs.forEach((input) => {
    input.checked = true;
    setTimeout(() => {
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.style.display = "none";
    }, 500);
  });

  // Newsletter

  const myForm = $(".newsletter-form-klaviyo");
  const klaviyoForm = $('.klaviyo-form-RKveXM');

  // Listen for the form submission event
  myForm.on('submit', function(event) {
    event.preventDefault(); // Prevent the form from submitting normally

    // Grab the value of the first form field
    const input1 = $(".newsletter-form-klaviyo .news__input");
    const data1 = input1.val();

    // Set the value of the second form field
    // const input2 = $('.klaviyo-form-RKveXM [type="email"]');
    // input2[0].value = data1;
    // console.log(input2.val());
    // console.log(input2[0].value);

    if(document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1")){
      document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1").value = data1;
    }

    // let klaviyoFormBtn = $('.needsclick [type="button"]');

    setTimeout(() => {
      console.log("test")
      if(document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1")){
        document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1").dispatchEvent(new Event('input'));
      }
      setTimeout(() => {
        // klaviyoFormBtn.trigger('click');
        if(document.querySelector("button.kl-private-reset-css-Xuajs1")){
          document.querySelector("button.kl-private-reset-css-Xuajs1").click()
        }
        setTimeout(() => {
          input1.val("");
          $(".form-success-klaviyo").attr('style', '');
        }, 500);
      }, 500);
      return
    }, 500);
  });

// end Newsletter

// Adding Arabic-Based Functionality
//   if (window.location.href.indexOf("/ar") !== -1) {
//     document.querySelector(".support__trigger").classList.add("bt-dropdown__arabic-lang");
//     // document.querySelector(".gm-retractor").classList.add("gm-retractor__arabic-lang");
//   } else {
//     document.querySelector(".support__trigger").classList.remove("bt-dropdown__arabic-lang");
//     // document.querySelector(".gm-retractor").classList.remove("gm-retractor__arabic-lang");
//   }

  const gmMenuElement = document.getElementById("nav_navigation");

  // Attach the event handler with the DOMSubtreeModified event
  // gmMenuElement.addEventListener("DOMSubtreeModified", function () {
  //   const gmRetractors = gmMenuElement.querySelectorAll(".gm-retractor");
  //   const gmIcons = gmMenuElement.querySelectorAll(".gm-icon");
  //
  //   // For all elements with .gm-retractor classes
  //   gmRetractors.forEach(function (element) {
  //     if (window.location.href.indexOf("/ar") !== -1) {
  //       element.classList.add("gm-retractor__arabic-lang");
  //     } else {
  //       element.classList.remove("gm-retractor__arabic-lang");
  //     }
  //   });
  //
  //   // For all elements with .gm-icon classes
  //   gmIcons.forEach(function (element) {
  //     if (window.location.href.indexOf("/ar") !== -1) {
  //       element.classList.add("gm-icon__arabic-lang");
  //     } else {
  //       element.classList.remove("gm-icon__arabic-lang");
  //     }
  //   });
  // });

  // For all elements with .wishlisthero-floating classes
  const wishListItems = document.querySelectorAll(".wishlisthero-floating");

  wishListItems.forEach(function (element) {
    if (window.location.href.indexOf("/ar") !== -1) {
      element.classList.add("wishlisthero-floating__arabic-lang");
    } else {
      element.classList.remove("wishlisthero-floating__arabic-lang");
    }
  });

  function updateLinks() {
    // Change the url address to arabic with the fragment /ar
    let currentURL = window.location.href;

    if (currentURL.includes("/ar")) {
      let headerLinks = document.getElementsByClassName("header__link");

      for (let i = 0; i < headerLinks.length; i++) {
        let link = headerLinks[i];
        let originalHref = link.getAttribute("href");

        if (originalHref.startsWith("https://trendgallery.art/") && !originalHref.includes("/ar/")) {
          link.setAttribute("href", originalHref.replace("https://trendgallery.art/", "https://trendgallery.art/ar/"));
        }
      }
    }
  }

  updateLinks();
  window.addEventListener("load", updateLinks);
});



$(document).ready(function(){
  $('.products-slider__content').each(function(){
    $(this).slick({
      adaptiveHeight: true,
      slidesToShow: 4,
      slidesToScroll: 1,
      nextArrow: $(this).prev().find('.slick-next'),
      prevArrow: $(this).prev().find('.slick-prev'),
      variableWidth: true,
      responsive: [
        {
          breakpoint: 767,
          settings: {
            dots: true
          }
        }
      ]
    });
  });
});

// check pin for image PDP
window.addEventListener('load', function() {
  $(".slick-slider").on("afterChange", function (event, slick, currentSlide, nextSlide){
    if(currentSlide  != 0){
      if(this.closest('.por')){
        if(this.closest('.por').querySelector('.image-mapper')){
          this.closest('.por').querySelector('.image-mapper').style.display = "none";
        }
      }
    }else{
      if(this.closest('.por')){
        if(this.closest('.por').querySelector('.image-mapper')){
          this.closest('.por').querySelector('.image-mapper').style.display = "unset";
        }
      }
    }
  });
});

$('.js-custom-wishlist-button').on('click',function (){
  $(this).toggleClass('active');
  $('.js-product-wishlist-btn').find('button').click();
});
$('.js-product-wishlist-btn').on('DOMSubtreeModified',function (){
  const svg = $(this).find('svg');
  $('.js-media-gallery-wishlist').show();
  if(svg.attr('data-prefix') == 'fas'){
    $('.js-custom-wishlist-button').addClass('active');
  }else{
    $('.js-custom-wishlist-button').removeClass('active');
  }
})
$(".js-star-rating").click(function() {
  $(".js-review-tab a").click();
  $([document.documentElement, document.body]).animate({
    scrollTop: $(".js-review-tab").offset().top
  }, 1500);
});


window.addEventListener("DOMContentLoaded", () => {
  // Display the block with the price above the product name
  let targetElements = document.querySelectorAll(".col-xs-12.col-md-7");
  let targetElement = targetElements[0];

  if (targetElement) {
    let highlightWrapper = targetElement.querySelector(".gr-highlight__wrapper");
    let priceWrapper = document.querySelector(".gr-wrapper__price");

    if (highlightWrapper && priceWrapper) {
      highlightWrapper.parentNode.insertBefore(priceWrapper, highlightWrapper.nextSibling);
    }
  }

  // Checkbox is checked by default
  let termsCheckInputs = document.querySelectorAll("input.terms-check");

  termsCheckInputs.forEach((input) => {
    input.checked = true;
    setTimeout(() => {
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.style.display = "none";
    }, 500);
  });

  // Newsletter

  const myForm = $(".newsletter-form-klaviyo");
  const klaviyoForm = $('.klaviyo-form-RKveXM');

  // Listen for the form submission event
  myForm.on('submit', function(event) {
    event.preventDefault(); // Prevent the form from submitting normally

    // Grab the value of the first form field
    const input1 = $(".newsletter-form-klaviyo .news__input");
    const data1 = input1.val();

    // Set the value of the second form field
    // const input2 = $('.klaviyo-form-RKveXM [type="email"]');
    // input2[0].value = data1;
    // console.log(input2.val());
    // console.log(input2[0].value);

    if(document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1")){
      document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1").value = data1;
    }

    // let klaviyoFormBtn = $('.needsclick [type="button"]');

    setTimeout(() => {
      console.log("test")
      if(document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1")){
        document.querySelector("input.needsclick.kl-private-reset-css-Xuajs1").dispatchEvent(new Event('input'));
      }
      setTimeout(() => {
        // klaviyoFormBtn.trigger('click');
        if(document.querySelector("button.kl-private-reset-css-Xuajs1")){
          document.querySelector("button.kl-private-reset-css-Xuajs1").click()
        }
        setTimeout(() => {
          input1.val("");
          $(".form-success-klaviyo").attr('style', '');
        }, 500);
      }, 500);
      return
    }, 500);
  });

// end Newsletter

// Adding Arabic-Based Functionality
//   if (window.location.href.indexOf("/ar") !== -1) {
//     document.querySelector(".support__trigger").classList.add("bt-dropdown__arabic-lang");
//     // document.querySelector(".gm-retractor").classList.add("gm-retractor__arabic-lang");
//   } else {
//     document.querySelector(".support__trigger").classList.remove("bt-dropdown__arabic-lang");
//     // document.querySelector(".gm-retractor").classList.remove("gm-retractor__arabic-lang");
//   }

  // const gmMenuElement = document.getElementById("nav_navigation");
  //
  // // Attach the event handler with the DOMSubtreeModified event
  // gmMenuElement.addEventListener("DOMSubtreeModified", function () {
  //   const gmRetractors = gmMenuElement.querySelectorAll(".gm-retractor");
  //   const gmIcons = gmMenuElement.querySelectorAll(".gm-icon");
  //
  //   // For all elements with .gm-retractor classes
  //   gmRetractors.forEach(function (element) {
  //     if (window.location.href.indexOf("/ar") !== -1) {
  //       element.classList.add("gm-retractor__arabic-lang");
  //     } else {
  //       element.classList.remove("gm-retractor__arabic-lang");
  //     }
  //   });
  //
  //   // For all elements with .gm-icon classes
  //   gmIcons.forEach(function (element) {
  //     if (window.location.href.indexOf("/ar") !== -1) {
  //       element.classList.add("gm-icon__arabic-lang");
  //     } else {
  //       element.classList.remove("gm-icon__arabic-lang");
  //     }
  //   });
  // });

  // For all elements with .wishlisthero-floating classes
  const wishListItems = document.querySelectorAll(".wishlisthero-floating");

  wishListItems.forEach(function (element) {
    if (window.location.href.indexOf("/ar") !== -1) {
      element.classList.add("wishlisthero-floating__arabic-lang");
    } else {
      element.classList.remove("wishlisthero-floating__arabic-lang");
    }
  });

  function updateLinks() {
    // Change the url address to arabic with the fragment /ar
    let currentURL = window.location.href;

    if (currentURL.includes("/ar")) {
      let headerLinks = document.getElementsByClassName("header__link");

      for (let i = 0; i < headerLinks.length; i++) {
        let link = headerLinks[i];
        let originalHref = link.getAttribute("href");

        if (originalHref.startsWith("https://trendgallery.art/") && !originalHref.includes("/ar/")) {
          link.setAttribute("href", originalHref.replace("https://trendgallery.art/", "https://trendgallery.art/ar/"));
        }
      }
    }
  }

  updateLinks();
  window.addEventListener("load", updateLinks);
});



$(document).ready(function(){
  $('.products-slider__content').each(function(){
    $(this).slick({
      adaptiveHeight: true,
      slidesToShow: 4,
      slidesToScroll: 1,
      nextArrow: $(this).prev().find('.slick-next'),
      prevArrow: $(this).prev().find('.slick-prev'),
      variableWidth: true,
      responsive: [
        {
          breakpoint: 767,
          settings: {
            dots: true
          }
        }
      ]
    });
  });
});

// check pin for image PDP
window.addEventListener('load', function() {
  $(".slick-slider").on("afterChange", function (event, slick, currentSlide, nextSlide){
    if(currentSlide  != 0){
      if(this.closest('.por')){
        if(this.closest('.por').querySelector('.image-mapper')){
          this.closest('.por').querySelector('.image-mapper').style.display = "none";
        }
      }
    }else{
      if(this.closest('.por')){
        if(this.closest('.por').querySelector('.image-mapper')){
          this.closest('.por').querySelector('.image-mapper').style.display = "unset";
        }
      }
    }
  });
});



function grDoScrolling(e,t){var o,n=window.pageYOffset,l=e-n;window.requestAnimationFrame((function e(s){o||(o=s);var i=s-o,c=Math.min(i/t,1);window.scrollTo(0,n+l*c),i<t&&window.requestAnimationFrame(e)}))}function grGetOffset(e){const t=e.getBoundingClientRect();return{left:t.left+window.scrollX,top:t.top+window.scrollY}}document.addEventListener("DOMContentLoaded",(function(){let e=setInterval((function(){if(1==t)clearInterval(e);else if(document.querySelector(".star_rewiews")){console.log("gr found rev");var o=document.querySelector(".star_rewiews"),n=0;o.addEventListener("click",(function(e){console.log("gr scroll to"),document.querySelector(".pg__tabs")&&(n=grGetOffset(document.querySelector(".pg__tabs")).top+100,document.documentElement.clientWidth<=500&&(n+=100)),o.setAttribute("data-id","vitals-tabs-container"),document.querySelector("a[data-toggle='tab'][title='Reviews']")&&(console.log("gr scroll click"),document.querySelector("a[data-toggle='tab'][title='Reviews']").click()),grDoScrolling(n,1e3)})),t=!0}}),100),t=!1})),$(document).ready((function(){let e=setInterval((function(){1==t?clearInterval(e):$(".bundle-aggregated_reviews").length>0&&($(".bundle-aggregated_reviews").wrapAll('<div class="star_rewiews">'),t=!0)}),100),t=!1;$(document).on("click",".js-dropdown",(function(){const e=$(this),t=e.closest(".cf__item__content");t.find(".custom_list").find("li");t.find(".custom_list_dropdown").slideToggle(300).toggleClass("list_show"),e.hasClass("js_expand_list")?(e.removeClass("js_expand_list"),e.find(".title").text("more+")):(e.addClass("js_expand_list"),e.find(".title").text("less-"))})),$(".category_faq .box .question").on("click",(function(){const e=$(this),t=e.closest(".box").find(".answer");e.toggleClass("open"),t.slideToggle(300),e.find(".icon_closed").toggleClass("op_none"),e.find(".icon_opened").toggleClass("op_none")}))}));